import * as THREE from 'three';
import {
    MIN_FRUSTUM_SIZE,
    LARGE_DISPLAY_THRESHOLD,
    OVERVIEW_FRUSTUM_TRANSITION_START
} from '../config/SheetConfig.js';

// No longer re-exporting constants - import directly from SheetConfig.js

// Event listener management utilities (delegated to ResourceManager)
export function addEventListener(element, type, handler, eventListeners) {
    const passiveEvents = ['touchstart', 'touchmove', 'touchend', 'wheel', 'mousewheel'];
    const options = passiveEvents.includes(type) ? { passive: true } : undefined;
    
    element.addEventListener(type, handler, options);
    eventListeners.push({ element, type, handler });
}

export function removeEventListeners(eventListeners) {
    eventListeners.forEach(({ element, type, handler }) => {
        element.removeEventListener(type, handler);
    });
    return [];
}

// Helper to determine if a given UV coordinate is over an image
export function isOverImage(uv, layout) {
    if (!uv) return false;
    
    const gridX = Math.floor((uv.x * layout.sheetWidth - layout.firstImageX) / (layout.imageWidth + layout.horizontalMargin));
    const gridY = Math.floor(((1 - uv.y) * layout.sheetHeight - layout.firstImageY) / (layout.imageHeight + layout.verticalMargin));
    
    if (gridX < 0 || gridX >= layout.columns || gridY < 0 || gridY >= layout.rows) {
        return false;
    }
    
    const imageX = layout.firstImageX + (gridX * (layout.imageWidth + layout.horizontalMargin));
    const imageY = layout.firstImageY + (gridY * (layout.imageHeight + layout.verticalMargin));
    
    const pixelX = uv.x * layout.sheetWidth;
    const pixelY = (1 - uv.y) * layout.sheetHeight;
    
    return pixelX >= imageX && 
           pixelX <= imageX + layout.imageWidth &&
           pixelY >= imageY && 
           pixelY <= imageY + layout.imageHeight;
}

// Removed unused utility functions:
// - calculateBounds: Not used anywhere
// - findNearestImage: ContactSheet has its own implementation
// - isDesktopOrTablet/isMobile: Not used anywhere

// Calculate zoom frustum for image detail view
export function calculateZoomFrustum() {
    // Responsive zoom levels - mobile stays perfect, desktop zooms in more
    // - Higher values (e.g., 0.7, 0.8) will make the image appear smaller (zoomed out)
    // - Lower values (e.g., 0.3, 0.4) will make the image appear larger (zoomed in)
    let zoomMultiplier;
    
    if (window.innerWidth <= 768) {
        // Mobile/tablet - keep the current perfect sizing
        zoomMultiplier = 0.7;
    } else {
        // Desktop - zoom in more for better image visibility
        zoomMultiplier = 0.4;
    }
    
    // Calculate target height based on zoom multiplier
    const targetHeight = window.innerHeight * zoomMultiplier;
    
    // Calculate the frustum size needed to achieve this
    // The image height is 900px, and we want it to be targetHeight pixels tall
    const frustumSize = (targetHeight / 900) * 2; // *2 because frustum is total height
    
    return {
        size: frustumSize,
        aspect: window.innerWidth / window.innerHeight
    };
}

export function calculateOverviewFrustum() {
    const aspect = window.innerWidth / window.innerHeight;
    const baseFrustumSize = aspect > 1 ? 4 : 4 / aspect;
    let minimumOverviewFrustum = baseFrustumSize;

    if (window.innerWidth >= LARGE_DISPLAY_THRESHOLD) {
        minimumOverviewFrustum = MIN_FRUSTUM_SIZE;
    } else if (window.innerWidth > OVERVIEW_FRUSTUM_TRANSITION_START) {
        const transitionProgress =
            (window.innerWidth - OVERVIEW_FRUSTUM_TRANSITION_START) /
            (LARGE_DISPLAY_THRESHOLD - OVERVIEW_FRUSTUM_TRANSITION_START);
        minimumOverviewFrustum =
            4 + (MIN_FRUSTUM_SIZE - 4) * transitionProgress;
    }

    const size = Math.max(baseFrustumSize, minimumOverviewFrustum);

    return { size, aspect };
}

/**
 * shareNative({ title?, text?, url? })
 * -----------------------------------
 * Shows the native share sheet if possible.
 * Returns Promise<true|false>   // true = shared (or copied); false = user cancelled / not supported
 */
export async function shareNative({
    title = document.title,
    text  = '',
    url   = window.location.href
  } = {}) {
  
    /* ---------- 1) Web Share API ---------- */
    const data = { title, text, url };
    const hasNativeShare = !!navigator.share;
  
    if (hasNativeShare) {
      try {
        await navigator.share(data);      // ✅ native sheet
        return true;
      } catch (err) {
        // User cancelled → just return, do NOT fall through to mail
        if (err.name === 'AbortError' || err.name === 'NotAllowedError') {
          return false;
        }
  
        /* iOS throws DataError if url isn't https. Retry without url */
        if (err.name === 'DataError' && url) {
          try {
            await navigator.share({ title, text });
            return true;
          } catch (err2) {
            if (err2.name === 'AbortError' || err2.name === 'NotAllowedError') {
              return false;
            }
            console.error('Second share attempt failed:', err2);
          }
        }
        console.error('Web Share API failed:', err);
        // At this point we *know* the browser supports navigator.share but it errored for some reason
        // We choose NOT to drop to mailto (prevents the surprise Mail prompt you saw)
        return false;
      }
    }
  
    /* ---------- 2) Clipboard fallback ---------- */
    if (navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(url);
        alert('Link copied to clipboard');
        return true;
      } catch (err) {
        console.warn('Clipboard write failed:', err);
      }
    }
  
    /* ---------- 3) Manual share via mail (legacy browsers only) ---------- */
    location.href =
      `mailto:?subject=${encodeURIComponent(title)}` +
      `&body=${encodeURIComponent(text ? `${text}\n${url}` : url)}`;
  
    return false;
  }
  